/**
 * CV Creator Page
 * 
 * Create tailored IOD PARC CVs by:
 * 1. Selecting an existing CV from database OR uploading a new CV
 * 2. Providing a role description (text)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Container, Box, Typography, Button, Paper, LinearProgress, Alert, Snackbar,
    Divider, Chip, Stack, CircularProgress, TextField, Autocomplete, Tabs, Tab
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Download as DownloadIcon,
    CheckCircle as CheckCircleIcon,
    Refresh as RefreshIcon,
    Close as CloseIcon,
    Person as PersonIcon,
    Work as WorkIcon,
    AutoFixHigh as AutoFixHighIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { searchCVsByName, ExistingCV } from '../services/similarCVs';
import { createTailoredCV, CreateTailoredCVRequest, CreateTailoredCVResult } from '../services/cvCreator';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

const CVCreatorPage: React.FC = () => {
    // CV selection state
    const [cvSourceTab, setCvSourceTab] = useState(0); // 0 = database, 1 = upload
    const [cvOptions, setCvOptions] = useState<ExistingCV[]>([]);
    const [selectedCV, setSelectedCV] = useState<ExistingCV | null>(null);
    const [isLoadingCVs, setIsLoadingCVs] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [uploadedCVFile, setUploadedCVFile] = useState<File | null>(null);
    
    // Role description state (text only)
    const [roleDescriptionText, setRoleDescriptionText] = useState('');
    
    // Generation state
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');
    const [generationResult, setGenerationResult] = useState<CreateTailoredCVResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    
    // UI state
    const [isDragActive, setIsDragActive] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Search CVs as user types (debounced)
    useEffect(() => {
        if (searchInput.length < 2) {
            setCvOptions([]);
            return;
        }
        
        const timeoutId = setTimeout(async () => {
            setIsLoadingCVs(true);
            try {
                const cvs = await searchCVsByName(searchInput, 'associate');
                setCvOptions(cvs);
            } catch (error) {
                console.error('Failed to search CVs:', error);
            } finally {
                setIsLoadingCVs(false);
            }
        }, 300);
        
        return () => clearTimeout(timeoutId);
    }, [searchInput]);

    const isValidCVFile = (file: File) => {
        const validTypes = [
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
            'application/pdf',
        ];
        return validTypes.includes(file.type) && file.size <= 10 * 1024 * 1024;
    };

    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setIsDragActive(true);
        } else if (e.type === 'dragleave') {
            setIsDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (isValidCVFile(file)) {
                setUploadedCVFile(file);
                setSelectedCV(null);
                setError(null);
            } else {
                setError('Please select a valid CV document (.docx or .pdf, max 10MB)');
            }
        }
    }, []);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (isValidCVFile(file)) {
                setUploadedCVFile(file);
                setSelectedCV(null);
                setError(null);
            } else {
                setError('Please select a valid CV document (.docx or .pdf, max 10MB)');
                e.target.value = '';
            }
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const hasCVSelected = () => {
        return cvSourceTab === 0 ? selectedCV !== null : uploadedCVFile !== null;
    };

    const canGenerate = () => {
        return hasCVSelected() && roleDescriptionText.trim().length >= 50;
    };

    const generateTailoredCV = async () => {
        if (!canGenerate()) return;

        setIsGenerating(true);
        setProgress(0);
        setProgressMessage('Starting CV generation...');
        setError(null);
        setGenerationResult(null);

        try {
            setProgress(10);
            setProgressMessage('Preparing request...');

            const request: CreateTailoredCVRequest = {
                cv_id: cvSourceTab === 0 ? selectedCV!.id : 0, // 0 indicates uploaded file
                role_description: roleDescriptionText,
                cv_file: cvSourceTab === 1 ? uploadedCVFile! : undefined
            };

            setProgress(20);
            setProgressMessage('Analyzing role requirements...');

            const result = await createTailoredCV(request, (progressUpdate) => {
                setProgress(progressUpdate.progress);
                setProgressMessage(progressUpdate.message);
            });

            setProgress(100);
            setProgressMessage('CV generated successfully!');
            setGenerationResult(result);
            setSnackbarMessage('Tailored CV created successfully!');
            setSnackbarOpen(true);

        } catch (error) {
            console.error('Generation error:', error);
            setError(error instanceof Error ? error.message : 'CV generation failed');
            setProgress(0);
            setProgressMessage('');
        } finally {
            setIsGenerating(false);
        }
    };

    const downloadGeneratedCV = async () => {
        if (!generationResult) return;

        try {
            if (generationResult.downloadUrl.startsWith('data:')) {
                const link = document.createElement('a');
                link.href = generationResult.downloadUrl;
                link.download = generationResult.convertedFilename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                const response = await fetch(generationResult.downloadUrl, {
                    method: 'GET',
                    credentials: 'include'
                });

                if (!response.ok) {
                    if (response.status === 401) {
                        throw new Error('Authentication required. Please log in again.');
                    }
                    if (response.status === 404) {
                        throw new Error('Download link expired. Please generate the CV again.');
                    }
                    throw new Error(`Download failed: ${response.status}`);
                }

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = generationResult.convertedFilename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }

            setSnackbarMessage('CV downloaded successfully!');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Download error:', error);
            setError(error instanceof Error ? error.message : 'Failed to download generated CV');
        }
    };

    const resetForm = () => {
        setRoleDescriptionText('');
        setUploadedCVFile(null);
        setSelectedCV(null);
        setSearchInput('');
        setGenerationResult(null);
        setProgress(0);
        setProgressMessage('');
        setError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <Container maxWidth="xl" sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        }}>
            {/* Header Section */}
            <Box sx={{ flexShrink: 0, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AutoFixHighIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
                    <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                        CV Creator
                    </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    Create a tailored IOD PARC CV by selecting or uploading a CV and providing a role description.
                </Typography>
            </Box>

            {/* Two-Column Layout */}
            <Box sx={{
                display: 'flex',
                gap: 4,
                flexGrow: 1,
                '@media (max-width: 1100px)': { flexDirection: 'column' }
            }}>
                {/* Left Column - Inputs */}
                <Box sx={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    
                    {/* Step 1: Select or Upload CV */}
                    <Paper sx={{
                        p: 3,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, rgba(25,118,210,0.06) 0%, rgba(25,118,210,0.03) 100%)',
                        border: '1px solid rgba(25,118,210,0.14)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Box sx={{
                                width: 32, height: 32, borderRadius: '8px',
                                backgroundColor: 'primary.main',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2
                            }}>
                                <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>1</Typography>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Select CV
                            </Typography>
                        </Box>

                        <Tabs
                            value={cvSourceTab}
                            onChange={(_, newValue) => {
                                setCvSourceTab(newValue);
                                setGenerationResult(null);
                            }}
                            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                        >
                            <Tab 
                                label="Search Database" 
                                icon={<SearchIcon />} 
                                iconPosition="start"
                                sx={{ minHeight: 48 }}
                            />
                            <Tab 
                                label="Upload New CV" 
                                icon={<CloudUploadIcon />} 
                                iconPosition="start"
                                sx={{ minHeight: 48 }}
                            />
                        </Tabs>

                        {/* Database Search Tab */}
                        <TabPanel value={cvSourceTab} index={0}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Type at least 2 characters to search for a CV by name.
                            </Typography>

                            <Autocomplete
                                options={cvOptions}
                                getOptionLabel={(option) => option.associate_name}
                                value={selectedCV}
                                inputValue={searchInput}
                                onInputChange={(_, newInputValue) => setSearchInput(newInputValue)}
                                onChange={(_, newValue) => {
                                    setSelectedCV(newValue);
                                    setUploadedCVFile(null);
                                    setGenerationResult(null);
                                }}
                                loading={isLoadingCVs}
                                filterOptions={(x) => x}
                                noOptionsText={searchInput.length < 2 ? "Type at least 2 characters" : "No CVs found"}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search for a CV by name"
                                        placeholder="e.g., Smith, John, Brown..."
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <PersonIcon sx={{ color: 'text.secondary', mr: 1 }} />
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {isLoadingCVs ? <CircularProgress size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <Box>
                                            <Typography variant="body1">{option.associate_name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.folder_name}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                            />

                            {selectedCV && (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                    Selected: <strong>{selectedCV.associate_name}</strong> ({selectedCV.folder_name})
                                </Alert>
                            )}
                        </TabPanel>

                        {/* Upload CV Tab */}
                        <TabPanel value={cvSourceTab} index={1}>
                            <Paper
                                sx={{
                                    p: 4,
                                    border: '2px dashed',
                                    borderColor: isDragActive ? 'primary.main' : 'rgba(255, 255, 255, 0.2)',
                                    backgroundColor: isDragActive ? 'rgba(25, 118, 210, 0.05)' : 'rgba(255, 255, 255, 0.02)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    borderRadius: '12px',
                                    textAlign: 'center',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        backgroundColor: 'rgba(25, 118, 210, 0.03)'
                                    }
                                }}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={handleUploadClick}
                            >
                                <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                                <Typography variant="body1" gutterBottom>
                                    {uploadedCVFile 
                                        ? uploadedCVFile.name 
                                        : 'Drop your CV file here or click to browse'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Supports .docx and .pdf files up to 10MB
                                </Typography>

                                {uploadedCVFile && (
                                    <Chip
                                        label={`${(uploadedCVFile.size / 1024).toFixed(1)} KB`}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ mt: 2 }}
                                        onDelete={(e) => {
                                            e.stopPropagation();
                                            setUploadedCVFile(null);
                                            if (fileInputRef.current) {
                                                fileInputRef.current.value = '';
                                            }
                                        }}
                                        deleteIcon={<CloseIcon />}
                                    />
                                )}
                            </Paper>
                        </TabPanel>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".docx,.pdf"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                    </Paper>

                    {/* Step 2: Role Description (Text Only) */}
                    <Paper sx={{
                        p: 3,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, rgba(156,39,176,0.06) 0%, rgba(156,39,176,0.03) 100%)',
                        border: '1px solid rgba(156,39,176,0.14)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Box sx={{
                                width: 32, height: 32, borderRadius: '8px',
                                backgroundColor: 'secondary.main',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2
                            }}>
                                <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>2</Typography>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Provide Role Description
                            </Typography>
                        </Box>

                        <TextField
                            multiline
                            rows={8}
                            fullWidth
                            placeholder="Paste the role description, terms of reference, or job requirements here...

The AI will use this to tailor the CV's profile/bio section. Include:

• What type of expert you're looking for (e.g., 'Senior M&E Specialist with 15+ years experience')
• Key sector expertise required (e.g., health, WASH, education, governance)
• Specific technical skills or methodologies needed
• Geographic or regional experience requirements
• Required qualifications and language skills
• Any specific organizational experience (e.g., UNICEF, DFID, World Bank)"
                            value={roleDescriptionText}
                            onChange={(e) => {
                                setRoleDescriptionText(e.target.value);
                                setGenerationResult(null);
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                        />
                        {roleDescriptionText.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                {roleDescriptionText.length} characters
                                {roleDescriptionText.length < 50 && ' (minimum 50 characters required)'}
                            </Typography>
                        )}
                    </Paper>
                </Box>

                {/* Right Column - Generate and Results */}
                <Box sx={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    
                    {/* Action Section */}
                    <Paper sx={{
                        p: 3,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, rgba(76,175,80,0.06) 0%, rgba(76,175,80,0.03) 100%)',
                        border: '1px solid rgba(76,175,80,0.14)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Box sx={{
                                width: 32, height: 32, borderRadius: '8px',
                                backgroundColor: 'success.main',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2
                            }}>
                                <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>3</Typography>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Generate Tailored CV
                            </Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            The AI will analyze the role requirements and create a tailored IOD PARC CV 
                            that emphasizes the most relevant experience and skills.
                        </Typography>

                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={generateTailoredCV}
                                disabled={!canGenerate() || isGenerating}
                                startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
                                sx={{
                                    minWidth: 200,
                                    height: 56,
                                    borderRadius: '12px',
                                    background: canGenerate() && !isGenerating
                                        ? 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)'
                                        : 'rgba(255, 255, 255, 0.1)',
                                    boxShadow: canGenerate() && !isGenerating
                                        ? '0 4px 20px rgba(76, 175, 80, 0.3)'
                                        : 'none',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        background: canGenerate() && !isGenerating
                                            ? 'linear-gradient(45deg, #43a047 30%, #4caf50 90%)'
                                            : 'rgba(255, 255, 255, 0.1)',
                                    }
                                }}
                            >
                                {isGenerating ? 'Generating...' : 'Generate Tailored CV'}
                            </Button>

                            {generationResult && (
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={resetForm}
                                    startIcon={<RefreshIcon />}
                                    sx={{ height: 56, borderRadius: '12px', borderWidth: 2, textTransform: 'none' }}
                                >
                                    Start New
                                </Button>
                            )}
                        </Stack>

                        {/* Progress Display */}
                        {isGenerating && (
                            <Box sx={{ mt: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <CircularProgress size={20} sx={{ mr: 1.5, color: 'success.main' }} />
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                                        Creating Your Tailored CV
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        '& .MuiLinearProgress-bar': {
                                            background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
                                            borderRadius: 4
                                        }
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1.5 }}>
                                    {progressMessage} ({Math.round(progress)}%)
                                </Typography>
                            </Box>
                        )}

                        {/* Checklist */}
                        {!isGenerating && !generationResult && (
                            <Box sx={{ mt: 3, p: 2, borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.03)' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                    Ready to generate when:
                                </Typography>
                                <Stack spacing={1}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {hasCVSelected() ? (
                                            <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                        ) : (
                                            <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid', borderColor: 'text.secondary' }} />
                                        )}
                                        <Typography variant="body2" color={hasCVSelected() ? 'success.main' : 'text.secondary'}>
                                            CV selected or uploaded
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {roleDescriptionText.length >= 50 ? (
                                            <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                        ) : (
                                            <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid', borderColor: 'text.secondary' }} />
                                        )}
                                        <Typography variant="body2" color={roleDescriptionText.length >= 50 ? 'success.main' : 'text.secondary'}>
                                            Role description provided (50+ characters)
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        )}
                    </Paper>

                    {/* Error Display */}
                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                borderRadius: '12px',
                                backgroundColor: 'rgba(244, 67, 54, 0.05)',
                                border: '1px solid rgba(244, 67, 54, 0.2)'
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Result Display */}
                    {generationResult && (
                        <Paper sx={{
                            p: 3,
                            borderRadius: '16px',
                            boxShadow: '0px 6px 18px rgba(0,0,0,0.12)',
                            border: '1px solid rgba(76,175,80,0.25)',
                            background: 'rgba(76,175,80,0.04)'
                        }}>
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    Tailored CV Ready
                                </Typography>
                            </Stack>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {generationResult.convertedFilename}
                            </Typography>

                            {generationResult.associateName && (
                                <Chip
                                    icon={<PersonIcon />}
                                    label={generationResult.associateName}
                                    size="small"
                                    sx={{ mb: 2 }}
                                />
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                onClick={downloadGeneratedCV}
                                startIcon={<DownloadIcon />}
                                sx={{
                                    height: 56,
                                    borderRadius: '12px',
                                    background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                                    boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                                        boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)'
                                    }
                                }}
                            >
                                Download Tailored CV
                            </Button>

                            {generationResult.temporary && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    This is a temporary download link. Please download your CV now.
                                </Alert>
                            )}
                        </Paper>
                    )}

                    {/* Empty State */}
                    {!isGenerating && !generationResult && !error && (
                        <Box sx={{
                            textAlign: 'center',
                            p: 4,
                            borderRadius: '16px',
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <WorkIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                Ready to Create
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
                                Select or upload a CV and provide a role description to generate a tailored IOD PARC CV
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
        </Container>
    );
};

export default CVCreatorPage;
